import React, { useCallback, useContext, useMemo, useEffect, useRef, useState } from 'react'
import { Iframe } from './components'

import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { AuthContext } from '@/layout/Auth'
import { getPurchasedApplications } from '@/api'
import { useQuery } from '@tanstack/react-query'
import { decodeUrl, encodeUrl, getChildPath, navbarModuleNavigator } from '@/utils'
import { Helmet } from 'react-helmet'
import { Message } from '@cx/ui'

const validUrl = (url: string) => {
  let href = ''
  if (url) {
    try {
      href = new URL(url)?.href
    } catch (e) {
      console.log(e)
    }
  }
  return href
}

const AppLoader: React.FC<unknown> = () => {
  const location = useLocation()
  const [pageTitle, setPageTitle] = useState('长亭百川云平台')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [validUrls, setValidUrls] = useState<string[]>()
  const { app_key = '' } = useParams()
  const [innerUrl, setInnerUrl] = useState<string>()
  const { authInfo } = useContext(AuthContext)
  const currentOrg = authInfo?.orgInfo
  const [needPrefix, setNeedPrefix] = useState<boolean>(false)
  const navigate = useNavigate()

  useQuery(
    ['getPurchasedApplicationsFromPanel', currentOrg?.id],

    () => {
      // @ts-ignore
      return getPurchasedApplications(currentOrg?.id)
    },
    {
      onSuccess: (res) => {
        setValidUrls(res?.apps?.map((item) => item?.url_prefix))
        const appName = res?.apps?.find(
          (r) => r?.url_prefix?.startsWith(`https://${app_key}`) || r?.url_prefix?.startsWith(`http://${app_key}`),
        )?.name
        if (appName && pageTitle.indexOf(appName) === -1) {
          setPageTitle([appName, pageTitle]?.join('-'))
        }
      },
      onError: () => {
        setValidUrls([])
      },
      enabled: !!currentOrg?.id,
    },
  )

  const validateUrl = useCallback(
    // @ts-ignore
    (inner: string): boolean => {
      if (authInfo?.auth) {
        // @ts-ignore
        if (validUrls?.length > 0) {
          const valid = validUrls?.findIndex((url) => inner?.includes(url)) ?? -1
          if (valid === -1) {
            return false
          } else {
            return true
          }
        }
      } else {
        return true
      }
    },
    [authInfo?.auth, validUrls],
  )

  const handleGetApplications = () => {
    if (!currentOrg?.id) {
      return
    } else {
      setNeedPrefix(true)
    }
  }

  const storageIframeSrc = useCallback(
    (inner_path: string) => {
      window.history.replaceState(
        '',
        '',
        `/console/app/${app_key}${inner_path === 'about:blank' ? '' : encodeUrl(inner_path)}`,
      )
    },
    [app_key],
  )

  const initUrlWatch = () => {
    // TODO: 需要同源，二级域名跨域的话需要设置document.domain
    try {
      document.domain = window.location.host
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
    const iFrame = document.getElementsByTagName('iframe')[0]
    const iWindow = iFrame.contentWindow
    if (!iWindow) {
      Message.error('未能获取到iframe windows')
      return
    }

    let iHistory
    try {
      iHistory = iWindow?.history as History
    } catch (e) {
      console.warn('document.domain 已经弃用, 请使用 postMessage 代替')
      window.addEventListener('message', (event) => {
        if (!innerUrl?.startsWith(event.origin) || event.data?.type !== 'location') {
          return
        }
        const { location } = event.data
        window.history.replaceState(
          '',
          '',
          `/console/app/${app_key}${location.pathname + location.search + location.hash}`,
        )
      })
      return
    }

    // hash
    iWindow.onhashchange = function (event) {
      storageIframeSrc(event.newURL)
    }
    // popstate
    iWindow.onpopstate = function (event) {
      // @ts-ignore
      storageIframeSrc(event?.currentTarget?.location?.href)
    }
    // load
    iFrame.onload = function () {
      storageIframeSrc(iWindow.location.href)
    }
    iHistory.replaceState = new Proxy(iHistory.replaceState, {
      apply: (target, thisArg, argArray) => {
        // trigger here what you need
        let path = argArray[2]
        if (path?.includes('http')) {
          path = encodeUrl(path)
        }
        storageIframeSrc(iWindow.location.origin + path)
        // @ts-ignore
        return target.apply(thisArg, argArray as unknown)
      },
    })
    iHistory.pushState = new Proxy(iHistory.pushState, {
      apply: (target, thisArg, argArray) => {
        // trigger here what you need
        let path = argArray[2]
        if (path?.includes('http')) {
          path = encodeUrl(path)
        }
        storageIframeSrc(iWindow.location.origin + path)
        // @ts-ignore
        return target.apply(thisArg, argArray as unknown)
      },
    })
  }

  const handleInnerUrl = (reload?: boolean) => {
    const appKeyArr = app_key.split('-')
    const org_id = appKeyArr[appKeyArr.length - 1]
    // 最后一位为数字，是组织id，没有组织 id 的情况, 默认使用当前组织 id
    if (isNaN(Number(org_id))) {
      const str = location.pathname
      if (!currentOrg?.id) {
        navbarModuleNavigator('/console/workbench', () => navigate('/console/workbench'))
        return
      }
      appKeyArr.push(currentOrg?.id.toString())
      const newStr = str.replace(app_key, appKeyArr.join('-'))
      navigate(newStr, { replace: true })
      return
    }

    const inner_path_real = decodeUrl(app_key, getChildPath(app_key))
    const url_real = decodeUrl(app_key, '')
    const realUrl = reload ? url_real : inner_path_real
    // @ts-ignore
    if (validUrl(realUrl) !== validUrl(innerUrl)) {
      setInnerUrl(realUrl)
    }
  }

  useEffect(
    () => {
      const inner_path = decodeUrl(app_key, getChildPath(app_key))
      console.log(inner_path, 'inner_path')
      if (!inner_path) {
        handleGetApplications()
      } else {
        // @ts-ignore
        handleInnerUrl(innerUrl && innerUrl?.length > 0)
      }
      return () => {
        setNeedPrefix(false)
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [app_key],
  )

  useEffect(() => {
    iframeRef.current?.addEventListener('load', initUrlWatch)
    return () => {
      iframeRef.current?.removeEventListener('load', initUrlWatch)
    }
  }, [innerUrl])

  console.log('innerUrl', innerUrl, pageTitle)

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Iframe key={innerUrl} ref={iframeRef} src={innerUrl} allow='clipboard-read; clipboard-write' />
    </>
  )
}

export default AppLoader
